import { forwardRef, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'

import { formatSVG } from '@aytm/app/stats/lib/render/svg-export'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'

const QuadrantChart = forwardRef(
  (
    {
      width,
      height,
      calcModelData,
      questionVariants,
      batches,
      scores: scoresProp,
      crossing,
      plots,
      title,
      dataLabels = 'hover',
      xAxisLimits,
      yAxisLimits,
      colorTheme,
      chartLegendMode,
      pointClick,
      chartLoad,
      chartRedraw,
      overlaySeries,
      questionKey,
      questionId,
      features,
      chartModeTypes = [],
      exportDelegated = false,
      onChartReady
    },
    ref
  ) => {
    // The caller may own the box -- it hands its ref in rather than wrapping this chart in another div.
    const innerContainerRef = useRef(null)
    const containerRef = ref || innerContainerRef
    const apiRef = useRef({})

    const answer = useMemo(() => calcModelData?.() || {}, [calcModelData])

    const scores = useMemo(() => {
      if (!scoresProp?.length) {
        return []
      }
      return [...scoresProp].sort((a, b) => a - b)
    }, [scoresProp])

    // PPTX bridge (pptx.coffee -> block.formatSVG). The svg transform lives here so both quadrant
    // callers share it: drop the gradient defs and the Highcharts UI buttons, then lay an opaque
    // backdrop so the export is not transparent. getContainer lets callers locate the chart.
    apiRef.current.getContainer = () => containerRef.current
    apiRef.current.formatSVG = () => {
      const source = containerRef.current?.querySelector('svg')
      if (!source) return null

      const svg = source.cloneNode(true)

      svg.querySelectorAll('linearGradient').forEach(node => node.remove())
      svg.querySelectorAll('.highcharts-button:not(.highcharts-breadcrumbs-button)').forEach(node => node.remove())

      const backdrop = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
      backdrop.setAttribute('x', '0')
      backdrop.setAttribute('y', '0')
      backdrop.setAttribute('width', source.width.baseVal.value)
      backdrop.setAttribute('height', source.height.baseVal.value)
      backdrop.setAttribute('fill', '#ffffff')
      svg.insertBefore(backdrop, svg.firstChild)

      return formatSVG(svg)
    }

    useEffect(() => {
      onChartReady?.(apiRef.current)
    }, [onChartReady])

    return (
      <ChartContainer
        ref={containerRef}
        chartMode="bubble"
        width={width}
        height={height}
        chartModeTypes={chartModeTypes}
        questionKey={questionKey}
        questionId={questionId}
        answer={answer}
        questionVariants={questionVariants}
        batches={batches}
        scores={scores}
        crossing={crossing}
        plots={plots}
        title={title}
        dataLabels={dataLabels}
        xAxisLimits={xAxisLimits}
        yAxisLimits={yAxisLimits}
        colorTheme={colorTheme}
        chartLegendMode={chartLegendMode}
        pointClick={pointClick}
        chartLoad={chartLoad}
        chartRedraw={chartRedraw}
        overlaySeries={overlaySeries}
        features={features}
        exportDelegated={exportDelegated}
      />
    )
  }
)

QuadrantChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  calcModelData: PropTypes.func.isRequired,
  questionVariants: PropTypes.array,
  batches: PropTypes.array,
  scores: PropTypes.arrayOf(PropTypes.number).isRequired,
  crossing: PropTypes.array,
  plots: PropTypes.array,
  title: PropTypes.string,
  dataLabels: PropTypes.oneOf(['off', 'on', 'hover']),
  xAxisLimits: PropTypes.array,
  yAxisLimits: PropTypes.array,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  pointClick: PropTypes.func,
  chartLoad: PropTypes.func,
  chartRedraw: PropTypes.func,
  overlaySeries: PropTypes.object,
  questionKey: PropTypes.string,
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  features: PropTypes.object,
  chartModeTypes: PropTypes.array,
  // Headless: a composite parent owns the export — parks ChartContainer's generic hook.
  exportDelegated: PropTypes.bool,
  // Hands the caller an api { formatSVG, getContainer } once mounted (pptx bridge + chart lookup).
  onChartReady: PropTypes.func
}

QuadrantChart.displayName = 'QuadrantChart'

export default QuadrantChart
